/**
 * Map Store - Zustand state management for 3D map view
 *
 * Manages:
 * - Current location and building selection
 * - Camera view settings (zoom, pitch, bearing)
 * - Map style preferences
 * - Mapbox configuration (labels, 3D features, terrain)
 * - View mode
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// View settings for camera position
export interface ViewSettings {
  center: [number, number] // [lng, lat]
  zoom: number
  pitch: number
  bearing: number
}

// Map style options
export type MapStyleId =
  | 'standard'
  | 'satellite-streets'
  | 'streets'
  | 'light'
  | 'dark'
  | 'satellite'

export interface MapStyle {
  id: MapStyleId
  name: string
  url: string
}

// Light preset options for Mapbox Standard style
export type LightPreset = 'dawn' | 'day' | 'dusk' | 'night'

// Theme options for Mapbox Standard style
export type MapTheme = 'default' | 'faded' | 'monochrome'

// Mapbox configuration structure
export interface MapboxConfig {
  // Light preset for time-of-day rendering
  lightPreset: LightPreset

  // Theme override
  theme: MapTheme

  // 3D Terrain Controls
  terrain: {
    enabled: boolean      // 3D terrain (hillshade/elevation)
    exaggeration: number  // 1.0 = realistic, 2.0 = exaggerated
  }

  // 3D Object Controls (Mapbox native 3D - disable to avoid conflicts with custom Three.js)
  objects3d: {
    enabled: boolean      // Master toggle for all Mapbox 3D objects
    buildings: boolean    // 3D buildings
    trees: boolean        // 3D tree models
    landmarks: boolean    // 3D landmark buildings
    facades: boolean      // Building facade details
  }

  // Label Visibility
  labels: {
    places: boolean       // City/town names
    roads: boolean        // Road names
    poi: boolean          // Points of interest
    transit: boolean      // Transit stations
    landmarks: boolean    // Landmark labels
  }

  // Feature Visibility
  features: {
    adminBoundaries: boolean  // State/country borders
    pedestrianRoads: boolean  // Paths/trails
  }
}

// Default Mapbox configuration optimized for FBO/airport use
export const DEFAULT_MAPBOX_CONFIG: MapboxConfig = {
  lightPreset: 'day',
  theme: 'default',

  terrain: {
    enabled: false,     // Off by default, user can enable
    exaggeration: 1.0,
  },

  objects3d: {
    enabled: false,     // Off by default (we render custom 3D hangars)
    buildings: false,   // Disabled - conflicts with Three.js hangars
    trees: false,
    landmarks: false,
    facades: false,
  },

  labels: {
    places: false,      // Disabled - clutter on airport view
    roads: false,       // Disabled - street names clutter airport view
    poi: false,         // Disabled - irrelevant for airport operations
    transit: false,     // Disabled - irrelevant for airport operations
    landmarks: false,   // Disabled - irrelevant for airport operations
  },

  features: {
    adminBoundaries: true,   // Keep for multi-location context
    pedestrianRoads: false,  // Disabled - not relevant on tarmac
  },
}

// Utility type for deep partial updates
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

// Available map styles
export const MAP_STYLES: MapStyle[] = [
  { id: 'standard', name: 'Standard', url: 'mapbox://styles/mapbox/standard' },
  { id: 'satellite-streets', name: 'Satellite Streets', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { id: 'streets', name: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'light', name: 'Light', url: 'mapbox://styles/mapbox/light-v11' },
  { id: 'dark', name: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
  { id: 'satellite', name: 'Satellite', url: 'mapbox://styles/mapbox/satellite-v9' },
]

// Default view settings (Detroit)
const DEFAULT_VIEW_SETTINGS: ViewSettings = {
  center: [-83.0051692342831, 42.410097026082155],
  zoom: 17,
  pitch: 60,
  bearing: 90,
}

// Store state interface
interface MapState {
  // Current selections
  currentLocationId: string | null
  selectedBuildingId: string | null

  // View settings
  viewSettings: ViewSettings
  mapStyleId: MapStyleId

  // Configuration namespace (extensible for future settings)
  config: {
    mapbox: MapboxConfig
  }

  // UI state
  isLoading: boolean

  // Actions
  setCurrentLocation: (locationId: string | null) => void
  setSelectedBuilding: (buildingId: string | null) => void
  setViewSettings: (settings: Partial<ViewSettings>) => void
  setMapStyle: (styleId: MapStyleId) => void
  setLoading: (loading: boolean) => void
  resetView: () => void

  // Mapbox config actions
  setMapboxConfig: (config: MapboxConfig) => void
  updateMapboxConfig: (updates: DeepPartial<MapboxConfig>) => void
}

// Helper function for deep merging config objects
const deepMerge = <T extends Record<string, unknown>>(
  target: T,
  source: DeepPartial<T>
): T => {
  const result = { ...target }
  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = target[key]
    if (
      sourceValue !== undefined &&
      typeof sourceValue === 'object' &&
      sourceValue !== null &&
      !Array.isArray(sourceValue) &&
      typeof targetValue === 'object' &&
      targetValue !== null
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as DeepPartial<Record<string, unknown>>
      ) as T[typeof key]
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue as T[typeof key]
    }
  }
  return result
}

export const useMapStore = create<MapState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        currentLocationId: null,
        selectedBuildingId: null,
        viewSettings: DEFAULT_VIEW_SETTINGS,
        mapStyleId: 'satellite-streets', // Default to satellite view for FBO operations
        isLoading: false,

        // Configuration namespace
        config: {
          mapbox: DEFAULT_MAPBOX_CONFIG,
        },

        // Actions
        setCurrentLocation: (locationId) =>
          set({ currentLocationId: locationId, selectedBuildingId: null }),

        setSelectedBuilding: (buildingId) =>
          set({ selectedBuildingId: buildingId }),

        setViewSettings: (settings) =>
          set((state) => ({
            viewSettings: { ...state.viewSettings, ...settings },
          })),

        setMapStyle: (styleId) =>
          set({ mapStyleId: styleId }),

        setLoading: (loading) =>
          set({ isLoading: loading }),

        resetView: () =>
          set({
            viewSettings: DEFAULT_VIEW_SETTINGS,
            selectedBuildingId: null,
          }),

        // Replace entire mapbox config
        setMapboxConfig: (config) =>
          set((state) => ({
            config: { ...state.config, mapbox: config },
          })),

        // Partially update mapbox config (deep merge)
        updateMapboxConfig: (updates) =>
          set((state) => ({
            config: {
              ...state.config,
              mapbox: deepMerge(state.config.mapbox, updates),
            },
          })),
      }),
      {
        name: 'planmax-map-style',
        partialize: (state) => ({ mapStyleId: state.mapStyleId }),
      }
    ),
    { name: 'map-store' }
  )
)

// Selector helpers
export const getMapStyleUrl = (styleId: MapStyleId): string => {
  const style = MAP_STYLES.find((s) => s.id === styleId)
  return style?.url || MAP_STYLES[0].url
}

// Check if style supports Mapbox Standard config properties
export const isStandardStyle = (styleId: MapStyleId): boolean => {
  return styleId === 'standard'
}
